import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API from "../api/axios";
import Layout from "../components/Layout";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";

export default function Settings() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { theme, mode, toggleTheme } = useTheme();
  const [form, setForm] = useState({ full_name: "", phone: "", address: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [notificationPrefs, setNotificationPrefs] = useState({
    notify_weekly_reports: true,
    notify_expiry_alerts: true,
    notify_low_stock: true,
    notify_announcements: true,
    notify_monthly_reports: true,
    notify_inactivity_reminder: true,
  });

  // Derived styles from theme — defined once, used everywhere
  const card = {
    background: theme.cardBg,
    border: `1px solid ${theme.cardBorder}`,
    borderRadius: "14px",
    padding: "28px",
    boxShadow: theme.cardShadow,
  };

  const inputStyle = {
    background: theme.inputBg,
    border: `1px solid ${theme.inputBorder}`,
    borderRadius: "10px",
    padding: "12px 16px",
    color: theme.textPrimary,
    fontSize: "14px",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  };

  useEffect(() => {
    API.get("/auth/profile").then(res => {
      setNotificationPrefs({
        notify_weekly_reports: res.data.notify_weekly_reports ?? true,
        notify_expiry_alerts: res.data.notify_expiry_alerts ?? true,
        notify_low_stock: res.data.notify_low_stock ?? true,
        notify_announcements: res.data.notify_announcements ?? true,
        notify_monthly_reports: res.data.notify_monthly_reports ?? true,
        notify_inactivity_reminder: res.data.notify_inactivity_reminder ?? true,
      });
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    API.get("/auth/profile")
      .then(res => {
        setForm({
          full_name: res.data.full_name || "",
          phone: res.data.phone || "",
          address: res.data.address || "",
        });
      })
      .catch(() => toast.error("Failed to load profile"))
      .finally(() => setLoading(false));
  }, []);

  const toggleNotification = async (key) => {
    const newValue = !notificationPrefs[key];
    setNotificationPrefs({ ...notificationPrefs, [key]: newValue });
    try {
      await API.put("/auth/profile", { [key]: newValue });
    } catch {
      toast.error("Failed to update notification preference");
      setNotificationPrefs({ ...notificationPrefs, [key]: !newValue });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await API.put("/auth/profile", form);
      localStorage.setItem("user_name", form.full_name);
      toast.success("Profile updated successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== "DELETE") return;
    setDeleting(true);
    try {
      await API.delete("/auth/delete-account");
      toast.success("Account deleted successfully");
      logout();
      navigate("/login");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete account");
      setDeleting(false);
    }
  };

  return (
    <Layout>
      <div style={{ marginBottom: "32px" }}>
        <h1 style={{ fontSize: "28px", fontWeight: "700", color: theme.textPrimary, letterSpacing: "-0.5px" }}>
          Settings
        </h1>
        <p style={{ fontSize: "14px", color: theme.textSecondary, marginTop: "4px" }}>
          Manage your account and business details
        </p>
      </div>

      {loading ? (
        <p style={{ color: theme.textSecondary }}>Loading...</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "24px", maxWidth: "560px" }}>

          {/* Business Profile */}
          <div style={card}>
            <h2 style={{ fontSize: "17px", fontWeight: "700", color: theme.textPrimary, marginBottom: "20px" }}>
              Business Profile
            </h2>
            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <label style={{ fontSize: "13px", fontWeight: "500", color: theme.textSecondary }}>Business Name</label>
                <input
                  type="text"
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  style={inputStyle}
                  required
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <label style={{ fontSize: "13px", fontWeight: "500", color: theme.textSecondary }}>Phone Number</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+234 800 000 0000"
                  style={inputStyle}
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <label style={{ fontSize: "13px", fontWeight: "500", color: theme.textSecondary }}>Business Address</label>
                <input
                  type="text"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="Lagos, Nigeria"
                  style={inputStyle}
                />
              </div>
              <button type="submit" disabled={saving} style={{
                background: "linear-gradient(135deg, #2ec4b6, #1a9e92)",
                color: "#0a0f0f", border: "none", borderRadius: "10px",
                padding: "12px 28px", fontSize: "14px", fontWeight: "700",
                alignSelf: "flex-start", cursor: "pointer",
              }}>
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </form>
          </div>

          {/* Appearance */}
          <div style={card}>
            <h2 style={{ fontSize: "17px", fontWeight: "700", color: theme.textPrimary, marginBottom: "20px" }}>
              Appearance
            </h2>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <p style={{ fontSize: "14px", fontWeight: "600", color: theme.textPrimary, marginBottom: "4px" }}>
                  {mode === "dark" ? "🌙 Dark Mode" : "☀️ Light Mode"}
                </p>
                <p style={{ fontSize: "12px", color: theme.textSecondary }}>
                  {mode === "dark"
                    ? "Switch to light mode for better visibility in daylight"
                    : "Switch to dark mode for low-light environments"}
                </p>
              </div>
              <div onClick={toggleTheme} style={{
                width: "52px", height: "28px", borderRadius: "14px",
                background: mode === "dark" ? "#1e3333" : "#2ec4b6",
                cursor: "pointer", position: "relative",
                transition: "background 0.2s", flexShrink: 0, marginLeft: "16px",
              }}>
                <div style={{
                  position: "absolute", top: "4px",
                  left: mode === "dark" ? "4px" : "28px",
                  width: "20px", height: "20px", borderRadius: "50%",
                  background: "#ffffff", transition: "left 0.2s",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "11px",
                }}>
                  {mode === "dark" ? "🌙" : "☀️"}
                </div>
              </div>
            </div>
          </div>

          {/* Email Notifications */}
          <div style={card}>
            <h3 style={{ fontSize: "16px", fontWeight: "700", color: theme.textPrimary, marginBottom: "8px" }}>
              Email Notifications
            </h3>
            <p style={{ fontSize: "13px", color: theme.textSecondary, marginBottom: "24px", lineHeight: "1.6" }}>
              Choose which emails you want to receive from MartSense.
            </p>
            {[
              { key: "notify_weekly_reports", label: "Weekly Performance Reports",
                desc: "Every Monday at 8 AM — revenue, profit and top products" },
              { key: "notify_expiry_alerts", label: "Subscription Expiry Alerts",
                desc: "Reminders at 7, 3 and 1 day before your subscription expires" },
              { key: "notify_low_stock", label: "Low Stock Alerts",
                desc: "Daily alert when products fall below their stock threshold" },
              { key: "notify_announcements", label: "Product Announcements",
                desc: "New features, updates and important notices from MartSense" },
              { key: "notify_monthly_reports", label: "Monthly Performance Reports",
                desc: "Sent on the 1st of every month — full month revenue and profit summary" },
              { key: "notify_inactivity_reminder", label: "Inactivity Reminders",
                desc: "Gentle nudge if your account has been inactive for 24 hours" },
            ].map((pref) => (
              <div key={pref.key} style={{
                display: "flex", justifyContent: "space-between",
                alignItems: "center", padding: "16px 0",
                borderBottom: `1px solid ${theme.divider}`,
              }}>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: "14px", fontWeight: "600", color: theme.textPrimary, margin: "0 0 4px" }}>
                    {pref.label}
                  </p>
                  <p style={{ fontSize: "12px", color: theme.textSecondary, margin: 0 }}>
                    {pref.desc}
                  </p>
                </div>
                <div onClick={() => toggleNotification(pref.key)} style={{
                  width: "48px", height: "26px", borderRadius: "13px",
                  background: notificationPrefs[pref.key] !== false ? "#21bdae" : theme.divider,
                  cursor: "pointer", position: "relative",
                  transition: "background 0.2s", flexShrink: 0, marginLeft: "16px",
                }}>
                  <div style={{
                    position: "absolute", top: "3px",
                    left: notificationPrefs[pref.key] !== false ? "25px" : "3px",
                    width: "20px", height: "20px", borderRadius: "50%",
                    background: "#ffffff", transition: "left 0.2s",
                  }} />
                </div>
              </div>
            ))}
          </div>

          {/* Security */}
          <div style={card}>
            <h2 style={{ fontSize: "17px", fontWeight: "700", color: theme.textPrimary, marginBottom: "20px" }}>
              Security
            </h2>
            <p style={{ fontSize: "14px", color: theme.textSecondary, marginBottom: "16px", lineHeight: "1.6" }}>
              To change your password use the Forgot Password feature on the login page.
            </p>
            <a href="/forgot-password" style={{ color: "#2ec4b6", textDecoration: "none", fontWeight: "600", fontSize: "14px" }}>
              Reset Password →
            </a>
          </div>

          {/* Danger Zone */}
          <div style={{
            marginTop: "16px",
            border: "1px solid #e74c3c33",
            borderRadius: "12px", padding: "24px",
            background: mode === "dark" ? "#1a0a0a" : "#fff5f5",
          }}>
            <h3 style={{
              fontSize: "14px", fontWeight: "700", color: "#e74c3c",
              textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px",
            }}>
              Danger Zone
            </h3>
            <p style={{ fontSize: "13px", color: theme.textSecondary, marginBottom: "20px", lineHeight: "1.6" }}>
              Permanently delete your MartSense account and all associated data including
              products, sales history, reports and team members. This action cannot be undone.
            </p>
            <button onClick={() => setShowDeleteModal(true)} style={{
              background: "none", border: "1px solid #e74c3c",
              borderRadius: "8px", padding: "10px 20px",
              color: "#e74c3c", fontSize: "14px", fontWeight: "600", cursor: "pointer",
            }}>
              Delete Account
            </button>
          </div>

        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 500, padding: "20px",
        }}>
          <div style={{
            background: theme.cardBg, border: "1px solid #e74c3c",
            borderRadius: "16px", padding: "32px",
            maxWidth: "420px", width: "100%",
          }}>
            <p style={{ fontSize: "28px", textAlign: "center", marginBottom: "8px" }}>⚠️</p>
            <h3 style={{ fontSize: "18px", fontWeight: "700", color: theme.textPrimary, textAlign: "center", marginBottom: "12px" }}>
              Delete Account Permanently?
            </h3>
            <p style={{ fontSize: "13px", color: theme.textSecondary, textAlign: "center", lineHeight: "1.7", marginBottom: "8px" }}>
              This will permanently delete your business account and all data including:
            </p>
            <ul style={{ fontSize: "13px", color: "#e74c3c", lineHeight: "1.8", paddingLeft: "20px", marginBottom: "20px" }}>
              <li>All products and categories</li>
              <li>All sales history and receipts</li>
              <li>All reports and analytics data</li>
              <li>All team members</li>
              <li>Your subscription</li>
            </ul>
            <p style={{ fontSize: "13px", fontWeight: "700", color: "#e74c3c", textAlign: "center", marginBottom: "24px" }}>
              This action CANNOT be undone.
            </p>
            <p style={{ fontSize: "13px", color: theme.textSecondary, marginBottom: "8px" }}>
              Type DELETE to confirm:
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="Type DELETE here"
              style={{ ...inputStyle, border: "1px solid #e74c3c", marginBottom: "20px" }}
            />
            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(""); }}
                style={{
                  flex: 1, background: "none",
                  border: `1px solid ${theme.cardBorder}`,
                  borderRadius: "8px", padding: "12px",
                  color: theme.textSecondary, fontSize: "14px", cursor: "pointer",
                }}
              >Cancel</button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== "DELETE" || deleting}
                style={{
                  flex: 1,
                  background: deleteConfirmText === "DELETE" ? "#e74c3c" : theme.divider,
                  border: "none", borderRadius: "8px", padding: "12px",
                  color: deleteConfirmText === "DELETE" ? "white" : theme.textSecondary,
                  fontSize: "14px", fontWeight: "700",
                  cursor: deleteConfirmText === "DELETE" ? "pointer" : "not-allowed",
                }}
              >
                {deleting ? "Deleting..." : "Delete Forever"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}